import { defineMessages } from 'react-intl';

export default defineMessages({
  pageTitle: {
    id: 'page.subscription.pageTitle',
    defaultMessage: 'Subscription',
  },
  // Plan / overview section
  planSectionTitle: {
    id: 'page.subscription.planSectionTitle',
    defaultMessage: 'Plan',
  },
  pricePerMonth: {
    id: 'page.subscription.pricePerMonth',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: '${price} / month',
  },
  overviewRenews: {
    id: 'page.subscription.overviewRenews',
    defaultMessage: 'Renews on {date, date, medium}',
  },
  overviewAccessUntil: {
    id: 'page.subscription.overviewAccessUntil',
    defaultMessage: 'Active until {date, date, medium}',
  },
  overviewTrialEnds: {
    id: 'page.subscription.overviewTrialEnds',
    defaultMessage: 'Trial ends {date, date, medium}',
  },
  // Usage section sub-groups + card tags
  billedTag: {
    id: 'page.subscription.billedTag',
    defaultMessage: 'Billed',
  },

  // --- Plan picker (/admin/subscription/plans) ---
  pickerTitle: {
    id: 'page.subscription.pickerTitle',
    defaultMessage: 'Choose your plan',
  },
  pickerTrialIntro: {
    id: 'page.subscription.pickerTrialIntro',
    defaultMessage:
      'Pick the plan to move to — upgrading starts it right away and ends your trial.',
  },
  // Grandfathered onto a retired tier: the account's plan has no card on this
  // page. Two things to land, in this order — the plan is not going anywhere,
  // and the page is still useful. It must NOT read as a warning: nothing is
  // wrong and no action is required.
  pickerRetiredCurrentPlan: {
    id: 'page.subscription.pickerRetiredCurrentPlan',
    defaultMessage:
      "You're on {plan}, which we no longer offer to new subscriptions. You can stay "
      + 'on it as long as you like — the plans below are what you can switch to.',
  },
  // Every tier retired at once. Only reachable by configuration, but a page
  // that renders empty is worse than one that says why it is empty.
  //
  // Two things it has to do, because this is the whole page in that state.
  // Reassure first: the reader arrived to change something and is about to be
  // told they cannot, so "your plan is unaffected" has to land before the
  // refusal. Then give the one route that IS open — `<link>` wraps the words
  // that were already there, so the sentence gained a destination rather than a
  // second CTA competing with itself. `<link>` is a react-intl rich-text tag,
  // supplied by the renderer; it sits MID-SENTENCE, so splitting this into
  // "text" + "link text" strings would hard-code English word order for every
  // translator.
  pickerNoPlansOffered: {
    id: 'page.subscription.pickerNoPlansOffered',
    defaultMessage:
      'There are no self-serve plans to switch to right now. Your current plan and '
      + 'everything on it are unaffected — <link>get in touch</link> about an '
      + 'Enterprise plan if you need to make a change.',
  },
  allPlansInclude: {
    id: 'page.subscription.allPlansInclude',
    defaultMessage: 'All plans include:',
  },
  currentPlanTag: {
    id: 'page.subscription.currentPlanTag',
    defaultMessage: 'Current plan',
  },
  pickerUpgradeCta: {
    id: 'page.subscription.pickerUpgradeCta',
    defaultMessage: 'Upgrade',
  },
  pickerSwitchCta: {
    id: 'page.subscription.pickerSwitchCta',
    defaultMessage: 'Switch',
  },
  pickerMetered: {
    id: 'page.subscription.pickerMetered',
    defaultMessage: 'No monthly fee — usage metered at list rates.',
  },
  // Metered plan WITH a monthly floor (PAYG since the $5 minimum). Follows
  // the copy rule at `planFromPerMonth`: framed as usable value, never as a
  // "minimum fee".
  pickerMeteredWithMinimum: {
    id: 'page.subscription.pickerMeteredWithMinimum',
    defaultMessage:
      // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
      'Usage billed at list rates. ${minimum} minimum a month, counted toward your usage.',
  },
  // Price slot for a $0-fee plan with a monthly floor (PAYG). "$0/mo" would
  // read as free and "From $5/mo" as a $5 fee; the floor is spent ON usage.
  comparePriceMinimum: {
    id: 'page.subscription.comparePriceMinimum',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: '${price} min / month',
  },
  // A metered-from-zero allowance cell shows the list rate itself, e.g.
  // "$0.08 / GB" — "Metered" alone told a buyer nothing about cost.
  compareRate: {
    id: 'page.subscription.compareRate',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: '${price} / {unit}',
  },
  // Enterprise column (static — CUSTOM plans are per-deal and deliberately
  // absent from the self-serve catalog).
  compareEnterpriseColumn: {
    id: 'page.subscription.compareEnterpriseColumn',
    defaultMessage: 'Enterprise',
  },
  compareCustom: {
    id: 'page.subscription.compareCustom',
    defaultMessage: 'Custom',
  },
  compareCommittedVolume: {
    id: 'page.subscription.compareCommittedVolume',
    defaultMessage: 'Committed volume',
  },
  // Enterprise's schema differentiator isn't the count — it's custom schema
  // authoring/customization (Enterprise-only capability).
  compareSchemaCustomizations: {
    id: 'page.subscription.compareSchemaCustomizations',
    defaultMessage: 'Schema customizations',
  },
  compareContactUs: {
    id: 'page.subscription.compareContactUs',
    defaultMessage: 'Contact us',
  },
  everythingInPlus: {
    id: 'page.subscription.everythingInPlus',
    defaultMessage: 'Everything in {plan}, plus {features}.',
  },
  everythingInMore: {
    id: 'page.subscription.everythingInMore',
    defaultMessage: 'Everything in {plan}, with higher limits.',
  },
  compareAllFeatures: {
    id: 'page.subscription.compareAllFeatures',
    defaultMessage: 'Compare plans',
  },
  changePlanButton: {
    id: 'page.subscription.changePlanButton',
    defaultMessage: 'Change plan',
  },
  // Baseline capabilities — part of the product on every tier rather than
  // per-plan addons, so they have no `plan_addon_defaults` code to be derived
  // from and are listed explicitly (see BASELINE_FEATURES in PlanPicker).
  featureCdn: {
    id: 'page.subscription.featureCdn',
    defaultMessage: 'CDN',
  },
  featureDamSchema: {
    id: 'page.subscription.featureDamSchema',
    defaultMessage: 'DAM + Schema',
  },
  featureAiAgentMcp: {
    id: 'page.subscription.featureAiAgentMcp',
    defaultMessage: 'AI Agent + MCP',
  },
  featureRemoveBackground: {
    id: 'page.subscription.featureRemoveBackground',
    defaultMessage: 'Background removal',
  },
  featureImageAnalysis: {
    id: 'page.subscription.featureImageAnalysis',
    defaultMessage: 'Image analysis',
  },
  featureAiImageEdit: {
    id: 'page.subscription.featureAiImageEdit',
    defaultMessage: 'AI image edits',
  },
  featurePrintExport: {
    id: 'page.subscription.featurePrintExport',
    defaultMessage: 'Print export',
  },

  // --- Plan comparison (matrix rows; rendered inside the picker's
  // "Compare all features" fold). Rows are generated from the catalog:
  // allowances from `quotas`, feature ticks from each plan's `addons`. ---
  // Section rows splitting the matrix into what each plan includes per month
  // (allowances, or PAYG's list rates) and which features it has.
  compareUsageGroup: {
    id: 'page.subscription.compareUsageGroup',
    defaultMessage: 'Usage per month',
  },
  compareFeaturesGroup: {
    id: 'page.subscription.compareFeaturesGroup',
    defaultMessage: 'Features',
  },
  compareRawValue: {
    id: 'page.subscription.compareRawValue',
    defaultMessage: '{value}',
  },
  // An allowance of 0 means "metered from the first unit" when the dimension
  // carries an overage rate (PAYG) — NOT unlimited.
  compareMetered: {
    id: 'page.subscription.compareMetered',
    defaultMessage: 'Metered',
  },
  meteredAtListRates: {
    id: 'page.subscription.meteredAtListRates',
    defaultMessage: 'metered at list rates',
  },
  comparePrice: {
    id: 'page.subscription.comparePrice',
    defaultMessage: 'Price',
  },
  comparePricePerMonth: {
    id: 'page.subscription.comparePricePerMonth',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: '${price}/mo',
  },
  compareContributors: {
    id: 'page.subscription.compareContributors',
    defaultMessage: 'Contributor seats',
  },
  compareViewers: {
    id: 'page.subscription.compareViewers',
    defaultMessage: 'Free viewer seats',
  },
  compareStorage: {
    id: 'page.subscription.compareStorage',
    defaultMessage: 'Storage',
  },
  compareCdn: {
    id: 'page.subscription.compareCdn',
    defaultMessage: 'CDN delivery',
  },
  compareTranscode: {
    id: 'page.subscription.compareTranscode',
    defaultMessage: 'Video transcoding',
  },
  compareVideoInCredits: {
    id: 'page.subscription.compareVideoInCredits',
    defaultMessage: 'In credits, per minute',
  },
  compareAiCredits: {
    id: 'page.subscription.compareAiCredits',
    defaultMessage: 'Credits',
  },
  compareSchemas: {
    id: 'page.subscription.compareSchemas',
    defaultMessage: 'Asset schemas',
  },
  comparePrintExports: {
    id: 'page.subscription.comparePrintExports',
    defaultMessage: 'Print exports (CMYK) / month',
  },
  compareCollections: {
    id: 'page.subscription.compareCollections',
    defaultMessage: 'Collections',
  },
  compareShares: {
    id: 'page.subscription.compareShares',
    defaultMessage: 'Branded asset share pages',
  },
  // Plan-gated from Starter up (SHARE_ANALYTICS, rollout 32) — rides the
  // addon feature rows, keyed by FEATURE_LABELS.
  featureShareAnalytics: {
    id: 'page.subscription.featureShareAnalytics',
    defaultMessage: 'Share analytics',
  },
  // Derived viewer cap (PAYG): the real number is per account
  // (count × contributor seats), so the catalog cell states the rule.
  comparePerSeat: {
    id: 'page.subscription.comparePerSeat',
    defaultMessage: '{count} per contributor seat',
  },
  // Schema add-on (PAYG): included figure + what buying extras reaches.
  compareSchemasAddon: {
    id: 'page.subscription.compareSchemasAddon',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: '{included} included · +${price}/mo each (max {max})',
  },
  compareIncluded: {
    id: 'page.subscription.compareIncluded',
    defaultMessage: 'Included',
  },
  compareNotIncluded: {
    id: 'page.subscription.compareNotIncluded',
    defaultMessage: 'Not included',
  },
  compareFootnote: {
    id: 'page.subscription.compareFootnote',
    defaultMessage:
      'Usage beyond any plan’s allowance is billed at the Pay as you go rates. Extra '
      + 'contributor seats can be added to any plan.',
  },
  // Split out of the footnote above: it is not small print about the tiers on
  // this page, it is the way OFF this page for anyone the tiers do not fit.
  // Buried at 50% opacity in a run-on sentence about overage rates, it read as
  // a disclaimer rather than an invitation.
  // `<link>` is a react-intl rich-text tag, supplied by the renderer. The link
  // sits MID-SENTENCE, so splitting this into "text" + "link text" strings
  // would hard-code English word order for every translator.
  enterpriseFootnote: {
    id: 'page.subscription.enterpriseFootnote',
    defaultMessage:
      'Need more, or something bespoke? <link>Contact us</link> about an Enterprise plan.',
  },
  featureCampaignLinks: {
    id: 'page.subscription.featureCampaignLinks',
    defaultMessage: 'Campaign links builder',
  },
  featureDeliveryInsights: {
    id: 'page.subscription.featureDeliveryInsights',
    defaultMessage: 'Delivery insights',
  },
  featureAnalyticsIntegrations: {
    id: 'page.subscription.featureAnalyticsIntegrations',
    defaultMessage: 'Analytics integrations',
  },
  featureAiSchedules: {
    id: 'page.subscription.featureAiSchedules',
    defaultMessage: 'Scheduled agents',
  },
  featureFaceRecognition: {
    id: 'page.subscription.featureFaceRecognition',
    defaultMessage: 'Face recognition',
  },
  featureByoStorage: {
    id: 'page.subscription.featureByoStorage',
    defaultMessage: 'Bring your own storage',
  },
  featureDatabaseExport: {
    id: 'page.subscription.featureDatabaseExport',
    defaultMessage: 'Database export',
  },

  // --- Rate card (plan-structure v2 Phase 7) ---
  // What usage above the plan's allowance costs. Essential on PAYG (where
  // everything is metered) and useful context on every paid tier. Rates and
  // their units come from GET /api/v1/billing/plans — never hardcoded.
  rateCardTitle: {
    id: 'page.subscription.rateCardTitle',
    defaultMessage: 'List rates',
  },
  rateCardSubtitle: {
    id: 'page.subscription.rateCardSubtitle',
    defaultMessage:
      'What usage beyond your plan allowance costs. Charged at the end of each billing cycle.',
  },
  rateCardSubtitleTrial: {
    id: 'page.subscription.rateCardSubtitleTrial',
    defaultMessage:
      'When your trial ends you move to Pay as you go, where usage is billed at these rates. '
      + 'Choosing a plan includes an allowance of each.',
  },
  rateCardPerUnit: {
    id: 'page.subscription.rateCardPerUnit',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: '${price} per {unit}',
  },
  rateDim_storage_gb: {
    id: 'page.subscription.rateDimStorage',
    defaultMessage: 'Storage',
  },
  rateDim_transcode_minutes: {
    id: 'page.subscription.rateDimTranscode',
    defaultMessage: 'Video transcoding',
  },
  rateDim_cdn_bytes: {
    id: 'page.subscription.rateDimCdn',
    defaultMessage: 'CDN delivery',
  },
  rateDim_ai_credits: {
    id: 'page.subscription.rateDimAiCredits',
    defaultMessage: 'Credits',
  },
  rateCardVideoInCredits: {
    id: 'page.subscription.rateCardVideoInCredits',
    defaultMessage: 'From {from, plural, one {# credit} other {# credits}} / min',
  },

  // --- "What do AI credits buy?" modal ---
  // Row labels are the release whitelist (see AiCreditCostsModal); the
  // numbers come from GET /api/v1/billing/ai-rates, never from copy.
  aiCostsLink: {
    id: 'page.subscription.aiCostsLink',
    defaultMessage: 'What do credits buy?',
  },
  aiCostsModalTitle: {
    id: 'page.subscription.aiCostsModalTitle',
    defaultMessage: 'What credits buy',
  },
  aiCostsAnchorLine: {
    id: 'page.subscription.aiCostsAnchorLine',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: '1 credit = ${price}.',
  },
  aiCostsCredits: {
    id: 'page.subscription.aiCostsCredits',
    defaultMessage: '{count, plural, one {# credit} other {# credits}}',
  },
  aiCostsBgRemove: {
    id: 'page.subscription.aiCostsBgRemove',
    defaultMessage: 'Background removal',
  },
  aiCostsAutoTag: {
    id: 'page.subscription.aiCostsAutoTag',
    defaultMessage: 'Auto-tagging (image analysis)',
  },
  aiCostsFaceRec: {
    id: 'page.subscription.aiCostsFaceRec',
    defaultMessage: 'Face recognition',
  },
  // Mirrors the photo-edit panel's own entries, not the raw rate keys.
  aiCostsEnhanceGroup: {
    id: 'page.subscription.aiCostsEnhanceGroup',
    defaultMessage: 'AI image enhancement',
  },
  aiCostsAutoEnhance: {
    id: 'page.subscription.aiCostsAutoEnhance',
    defaultMessage: 'Auto enhance',
  },
  aiCostsRemoveObject: {
    id: 'page.subscription.aiCostsRemoveObject',
    defaultMessage: 'Remove object',
  },
  aiCostsAiRestore: {
    id: 'page.subscription.aiCostsAiRestore',
    defaultMessage: 'AI restore',
  },
  aiCostsCustomEnhance: {
    id: 'page.subscription.aiCostsCustomEnhance',
    defaultMessage: 'Custom enhancement (whole edit, one charge)',
  },
  aiCostsPreviewNote: {
    id: 'page.subscription.aiCostsPreviewNote',
    defaultMessage: 'Previews are charged at reduced rates.',
  },
  // Not AI — why the unit is "credits", not "AI credits" (2026-10-13).
  aiCostsProcessingGroup: {
    id: 'page.subscription.aiCostsProcessingGroup',
    defaultMessage: 'Processing',
  },
  aiCostsPrintExport: {
    id: 'page.subscription.aiCostsPrintExport',
    defaultMessage: 'Print export, beyond your plan’s monthly allowance',
  },
  aiCostsVideoGroup: {
    id: 'page.subscription.aiCostsVideoGroup',
    defaultMessage: 'Video, per minute of uploaded video',
  },
  aiCostsCreditsPerMinute: {
    id: 'page.subscription.aiCostsCreditsPerMinute',
    defaultMessage: '{count, plural, one {# credit} other {# credits}} / min',
  },
  aiCostsVideoSd: {
    id: 'page.subscription.aiCostsVideoSd',
    defaultMessage: 'SD MP4 (up to 720p)',
  },
  aiCostsVideoHd: {
    id: 'page.subscription.aiCostsVideoHd',
    defaultMessage: 'HD MP4 (1080p)',
  },
  aiCostsVideo4k: {
    id: 'page.subscription.aiCostsVideo4k',
    defaultMessage: '4K MP4 (above 1080p)',
  },
  aiCostsVideoHls: {
    id: 'page.subscription.aiCostsVideoHls',
    defaultMessage: 'Adaptive streaming (HLS, every quality up to 1080p)',
  },
  aiCostsVideoStoryboard: {
    id: 'page.subscription.aiCostsVideoStoryboard',
    defaultMessage: 'Storyboard',
  },
  aiCostsVideoNote: {
    id: 'page.subscription.aiCostsVideoNote',
    defaultMessage:
      'Metered to the second; each upload is rounded up to a whole credit. 100 minutes of adaptive streaming is about {hundredMinutes, number} credits.',
  },
  aiCostsAgent: {
    id: 'page.subscription.aiCostsAgent',
    defaultMessage: 'AI Agent',
  },
  aiCostsAgentApprox: {
    id: 'page.subscription.aiCostsAgentApprox',
    defaultMessage: '≈{count, plural, one {# credit} other {# credits}} per message',
  },
  aiCostsAgentDetail: {
    id: 'page.subscription.aiCostsAgentDetail',
    defaultMessage:
      'Billed by tokens: {input, plural, one {# credit} other {# credits}} per 1K input '
      + '· {output} per 1K output · {tool} per tool call.',
  },

  // --- Seats (plan-structure v2 Phase 6/7) ---
  // Seats are a hard cap, not an overage dimension: going over is refused at
  // invite time rather than billed, so the card wears this pill instead of
  // "Billed".
  seatCapTag: {
    id: 'page.subscription.seatCapTag',
    defaultMessage: 'Seat cap',
  },
  // Collections/Shares wear the same "hard cap, not billed" pill semantics
  // as seats, but "Seat cap" would be nonsense on them.
  planLimitTag: {
    id: 'page.subscription.planLimitTag',
    defaultMessage: 'Plan limit',
  },
  seatsOfContributorSeats: {
    id: 'page.subscription.seatsOfContributorSeats',
    defaultMessage: 'of {quota, plural, one {# contributor seat} other {# contributor seats}}',
  },
  seatsViewersLine: {
    id: 'page.subscription.seatsViewersLine',
    defaultMessage:
      '{used} of {quota, plural, one {# free viewer seat} other {# free viewer seats}}',
  },
  seatsViewersLineUnlimited: {
    id: 'page.subscription.seatsViewersLineUnlimited',
    defaultMessage: '{used} viewers (unlimited)',
  },
  seatsExtraNote: {
    id: 'page.subscription.seatsExtraNote',
    defaultMessage:
      '{included} included in your plan + {extra, plural, one {# extra seat} other {# extra seats}}',
  },
  seatsSectionTitle: {
    id: 'page.subscription.seatsSectionTitle',
    defaultMessage: 'Extra contributor seats',
  },
  seatsSectionSubtitle: {
    id: 'page.subscription.seatsSectionSubtitle',
    defaultMessage:
      'Your plan includes {included, plural, one {# contributor seat} other {# contributor seats}}'
      + ' — people who can upload and edit assets. Need more without changing plan? '
      // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
      + 'Extra seats are ${price}/month each. Viewers (read-only) do not use a contributor seat.',
  },
  seatsTotalLine: {
    id: 'page.subscription.seatsTotalLine',
    defaultMessage:
      '{total, plural, one {# contributor seat} other {# contributor seats}} total · {inUse} in use',
  },
  seatsCostIncrease: {
    id: 'page.subscription.seatsCostIncrease',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: '+${amount}/month',
  },
  seatsCostDecrease: {
    id: 'page.subscription.seatsCostDecrease',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: '−${amount}/month',
  },
  seatsUpdateCta: {
    id: 'page.subscription.seatsUpdateCta',
    defaultMessage: 'Update seats',
  },
  seatsCancelCta: {
    id: 'page.subscription.seatsCancelCta',
    defaultMessage: 'Cancel',
  },
  seatsProrationNote: {
    id: 'page.subscription.seatsProrationNote',
    defaultMessage:
      'You will be charged the pro-rated difference for the rest of this billing cycle.',
  },
  seatsUpdated: {
    id: 'page.subscription.seatsUpdated',
    defaultMessage:
      '{count, plural, =0 {Seats updated — no extra contributor seats.} '
      + 'one {Seats updated — # extra contributor seat.} '
      + 'other {Seats updated — # extra contributor seats.}}',
  },
  seatsUpdateFailed: {
    id: 'page.subscription.seatsUpdateFailed',
    defaultMessage: 'Could not update your seats. Please try again.',
  },
  // Derived viewer cap (PAYG): buying a seat is also how you get viewers.
  seatsPerSeatViewersNote: {
    id: 'page.subscription.seatsPerSeatViewersNote',
    defaultMessage:
      'Every contributor seat includes {count, plural, one {# free viewer seat} '
      + 'other {# free viewer seats}}.',
  },
  // PAYG asset-schema add-on (migration 54) — SchemasBody strings.
  schemasSectionTitle: {
    id: 'page.subscription.schemasSectionTitle',
    defaultMessage: 'Extra asset schemas',
  },
  schemasSectionSubtitle: {
    id: 'page.subscription.schemasSectionSubtitle',
    defaultMessage:
      'Your plan includes {included, plural, one {# asset schema} other {# asset schemas}}. '
      // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
      + 'Add more at ${price}/month each, up to {max} total. '
      + 'Custom schema authoring is an Enterprise feature.',
  },
  schemasTotalLine: {
    id: 'page.subscription.schemasTotalLine',
    defaultMessage:
      '{total, plural, one {# asset schema} other {# asset schemas}} total · {inUse} active',
  },
  schemasUpdateCta: {
    id: 'page.subscription.schemasUpdateCta',
    defaultMessage: 'Update schemas',
  },
  schemasUpdated: {
    id: 'page.subscription.schemasUpdated',
    defaultMessage:
      '{count, plural, =0 {Schemas updated — no extra asset schemas.} '
      + 'one {Schemas updated — # extra asset schema.} '
      + 'other {Schemas updated — # extra asset schemas.}}',
  },
  schemasUpdateFailed: {
    id: 'page.subscription.schemasUpdateFailed',
    defaultMessage: 'Could not update your asset schemas. Please try again.',
  },
  // Purchased quantity add-ons, listed under the plan on the overview card.
  planExtrasLabel: {
    id: 'page.subscription.planExtrasLabel',
    defaultMessage: 'Extras purchased:',
  },
  planExtrasSeats: {
    id: 'page.subscription.planExtrasSeats',
    defaultMessage:
      '{count, plural, one {# extra contributor seat} other {# extra contributor seats}}'
      // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
      + ' (+${amount}/mo)',
  },
  planExtrasSchemas: {
    id: 'page.subscription.planExtrasSchemas',
    defaultMessage:
      '{count, plural, one {# extra asset schema} other {# extra asset schemas}}'
      // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
      + ' (+${amount}/mo)',
  },
  // Folded wrapper holding "Change plan" + "Compare plans" (both sub-headings
  // inside it), sitting directly under Plan.
  // Manage plan section
  // Danger zone
  ownedBy: {
    id: 'page.subscription.ownedBy',
    defaultMessage: 'Owned by {email}',
  },
  ownedByYou: {
    id: 'page.subscription.ownedByYou',
    defaultMessage: 'You are the subscription owner.',
  },
  // Single CTA in the Plan overview; also the sub-heading over the tier buttons
  // inside Plan options, which is where they now live.
  choosePlanCta: {
    id: 'page.subscription.choosePlanCta',
    defaultMessage: 'Choose a plan',
  },
  upgradeStarting: {
    id: 'page.subscription.upgradeStarting',
    defaultMessage: 'Opening checkout…',
  },

  // --- The rail: actions + resources ---
  railActionsTitle: {
    id: 'page.subscription.railActionsTitle',
    defaultMessage: 'Billing actions',
  },
  railResourcesTitle: {
    id: 'page.subscription.railResourcesTitle',
    defaultMessage: 'Resources',
  },
  railTeamLink: {
    id: 'page.subscription.railTeamLink',
    defaultMessage: 'Team & users',
  },

  // Payment method + invoices (Phase 5, in-app)
  // Shown when the account has no Stripe Customer yet: there is nothing to
  // "manage", so the CTA opens a card-capturing checkout instead of the
  // portal (which would 503 for a customer that doesn't exist).
  addPaymentCta: {
    id: 'page.subscription.addPaymentCta',
    defaultMessage: 'Add payment method',
  },
  // Enterprise / negotiated plan with no Stripe Customer: billing runs through
  // an invoice against the contract, so there is no self-serve card to add.
  paymentMethodEnterprise: {
    id: 'page.subscription.paymentMethodEnterprise',
    defaultMessage:
      'Your plan is billed by invoice under your agreement with us. Contact your account '
      + 'manager or support to change billing details.',
  },
  updatePaymentCta: {
    id: 'page.subscription.updatePaymentCta',
    defaultMessage: 'Manage payment method',
  },
  updatePaymentStarting: {
    id: 'page.subscription.updatePaymentStarting',
    defaultMessage: 'Opening…',
  },
  paymentError: {
    id: 'page.subscription.paymentError',
    defaultMessage:
      "Couldn't open payment management. Please try again or contact FileSpin support.",
  },
  invoicesHeading: {
    id: 'page.subscription.invoicesHeading',
    defaultMessage: 'Invoices',
  },
  invoicesEmpty: {
    id: 'page.subscription.invoicesEmpty',
    defaultMessage: 'No invoices yet.',
  },
  invoiceDownload: {
    id: 'page.subscription.invoiceDownload',
    defaultMessage: 'View invoice',
  },
  // Shown only when the API reports `has_more`. Deliberately says "all", not
  // "more": the point is that this list is recent history and the portal is
  // the complete archive.
  invoicesViewAll: {
    id: 'page.subscription.invoicesViewAll',
    defaultMessage: 'View all invoices',
  },
  invoicesViewAllHint: {
    id: 'page.subscription.invoicesViewAllHint',
    defaultMessage: 'Showing your most recent invoices.',
  },
  checkoutError: {
    id: 'page.subscription.checkoutError',
    defaultMessage:
      "Couldn't start checkout. Please try again or contact FileSpin support.",
  },
  // Distinct from checkoutError, which is a transient "the request failed, try
  // again". This one means Stripe.js itself never loaded, so retrying the same
  // click cannot help: it is either a script blocker on the customer's side or a
  // misconfigured build on ours. Naming js.stripe.com is the only part the
  // customer can act on themselves.
  checkoutUnavailable: {
    id: 'page.subscription.checkoutUnavailable',
    defaultMessage:
      "Checkout couldn't load. If you use an ad or script blocker, allow "
      + 'js.stripe.com and reload the page — otherwise please contact FileSpin '
      + 'support and we will take payment another way.',
  },
  planChangesNonOwner: {
    id: 'page.subscription.planChangesNonOwner',
    defaultMessage: 'Plan changes — contact your subscription owner ({email}).',
  },

  // Change plan (Phase 4)
  // Deferred-change banner while pending_plan is set.
  pendingChangeBanner: {
    id: 'page.subscription.pendingChangeBanner',
    defaultMessage: 'Changing to {plan} on {effectiveAt, date, medium}.',
  },
  pendingChangeBannerNoDate: {
    id: 'page.subscription.pendingChangeBannerNoDate',
    defaultMessage: 'Changing to {plan} at your next renewal.',
  },
  keepCurrentPlanCta: {
    id: 'page.subscription.keepCurrentPlanCta',
    defaultMessage: 'Keep my current plan',
  },
  // Change-plan modal
  changePlanModalTitleUpgrade: {
    id: 'page.subscription.changePlanModalTitleUpgrade',
    defaultMessage: 'Upgrade to {plan}',
  },
  changePlanModalTitleDowngrade: {
    id: 'page.subscription.changePlanModalTitleDowngrade',
    defaultMessage: 'Switch to {plan}',
  },
  // Upgrade: immediate, prorated charge now.
  changePlanUpgradeBody: {
    id: 'page.subscription.changePlanUpgradeBody',
    defaultMessage:
      "You'll move to {plan} right away, with its higher limits available immediately. We'll charge the prorated difference for the rest of your current billing period now; your next renewal is at the {plan} rate.",
  },
  // Upgrade, priced by Stripe's invoice preview.
  changePlanUpgradeBodyAmount: {
    id: 'page.subscription.changePlanUpgradeBodyAmount',
    defaultMessage:
      "You'll move to {plan} right away, with its higher limits available immediately. You'll be charged {amount} now for the rest of your current billing period; your next renewal is at the {plan} rate.",
  },
  // The upgrade's immediate charge was declined or needs authentication (402).
  changePlanPaymentFailed: {
    id: 'page.subscription.changePlanPaymentFailed',
    defaultMessage:
      "The payment for this upgrade didn't go through, so your plan hasn't changed. Check your payment method and try again.",
  },
  // Downgrade: deferred to renewal, no charge now.
  changePlanDowngradeBody: {
    id: 'page.subscription.changePlanDowngradeBody',
    defaultMessage:
      'You keep your current plan until {effectiveAt, date, medium} (your next renewal), then move to {plan}. Nothing is charged now.',
  },
  changePlanDowngradeBodyNoDate: {
    id: 'page.subscription.changePlanDowngradeBodyNoDate',
    defaultMessage:
      'You keep your current plan until your next renewal, then move to {plan}. Nothing is charged now.',
  },
  changePlanConfirmUpgrade: {
    id: 'page.subscription.changePlanConfirmUpgrade',
    defaultMessage: 'Upgrade now',
  },
  changePlanConfirmDowngrade: {
    id: 'page.subscription.changePlanConfirmDowngrade',
    defaultMessage: 'Schedule change',
  },
  changePlanConfirming: {
    id: 'page.subscription.changePlanConfirming',
    defaultMessage: 'Working…',
  },
  changePlanCancel: {
    id: 'page.subscription.changePlanCancel',
    defaultMessage: 'Never mind',
  },
  changePlanError: {
    id: 'page.subscription.changePlanError',
    defaultMessage:
      "Couldn't change your plan. Please try again or contact FileSpin support.",
  },
  changePlanUpgradedToast: {
    id: 'page.subscription.changePlanUpgradedToast',
    defaultMessage: "You're now on the {plan} plan.",
  },
  changePlanScheduledToast: {
    id: 'page.subscription.changePlanScheduledToast',
    defaultMessage: 'Plan change scheduled — switching to {plan} at your next renewal.',
  },

  // Cancel / reactivate (Phase D+)
  cancelPlanCta: {
    id: 'page.subscription.cancelPlanCta',
    defaultMessage: 'Cancel plan',
  },
  cancelFooterNote: {
    id: 'page.subscription.cancelFooterNote',
    defaultMessage: "Cancelling keeps your plan active until the end of the current billing period; it then won't renew.",
  },
  reactivateCta: {
    id: 'page.subscription.reactivateCta',
    defaultMessage: 'Reactivate plan',
  },
  reactivateStarting: {
    id: 'page.subscription.reactivateStarting',
    defaultMessage: 'Reactivating…',
  },
  // Shown while status='cancelled' but access continues to period end.
  reactivateError: {
    id: 'page.subscription.reactivateError',
    defaultMessage:
      "Couldn't reactivate. Please try again or contact FileSpin support.",
  },

  // Cancel modal
  cancelModalTitle: {
    id: 'page.subscription.cancelModalTitle',
    defaultMessage: 'Cancel your plan',
  },
  cancelModalIntro: {
    id: 'page.subscription.cancelModalIntro',
    defaultMessage:
      "Your plan stays active until the end of the current billing period, then it won't renew. Let us know why you're leaving.",
  },
  cancelReasonLabel: {
    id: 'page.subscription.cancelReasonLabel',
    defaultMessage: 'Reason',
  },
  cancelReasonPlaceholder: {
    id: 'page.subscription.cancelReasonPlaceholder',
    defaultMessage: 'Select a reason…',
  },
  cancelReasonTooExpensive: {
    id: 'page.subscription.cancelReasonTooExpensive',
    defaultMessage: 'Too expensive',
  },
  cancelReasonMissingFeatures: {
    id: 'page.subscription.cancelReasonMissingFeatures',
    defaultMessage: 'Missing features I need',
  },
  cancelReasonSwitchingProvider: {
    id: 'page.subscription.cancelReasonSwitchingProvider',
    defaultMessage: 'Switching to another provider',
  },
  cancelReasonNotUsing: {
    id: 'page.subscription.cancelReasonNotUsing',
    defaultMessage: 'Not using it enough',
  },
  cancelReasonTemporary: {
    id: 'page.subscription.cancelReasonTemporary',
    defaultMessage: 'Just taking a break',
  },
  cancelReasonOther: {
    id: 'page.subscription.cancelReasonOther',
    defaultMessage: 'Other',
  },
  cancelDetailsLabel: {
    id: 'page.subscription.cancelDetailsLabel',
    defaultMessage: 'Anything else? (optional)',
  },
  cancelDetailsLabelRequired: {
    id: 'page.subscription.cancelDetailsLabelRequired',
    defaultMessage: 'Please tell us more',
  },
  cancelDetailsPlaceholder: {
    id: 'page.subscription.cancelDetailsPlaceholder',
    defaultMessage: 'Your feedback helps us improve.',
  },
  cancelModalKeepPlan: {
    id: 'page.subscription.cancelModalKeepPlan',
    defaultMessage: 'Keep my plan',
  },
  cancelModalConfirm: {
    id: 'page.subscription.cancelModalConfirm',
    defaultMessage: 'Cancel plan',
  },
  cancelModalConfirming: {
    id: 'page.subscription.cancelModalConfirming',
    defaultMessage: 'Cancelling…',
  },
  cancelModalError: {
    id: 'page.subscription.cancelModalError',
    defaultMessage:
      "Couldn't cancel. Please try again or contact FileSpin support.",
  },

  // Status badges — full lifecycle (deploy/database/conf/19-…).
  statusActive: {
    id: 'page.subscription.statusActive',
    defaultMessage: 'Active',
  },
  statusTrialing: {
    id: 'page.subscription.statusTrialing',
    defaultMessage: 'Trialing',
  },
  statusPastDue: {
    id: 'page.subscription.statusPastDue',
    defaultMessage: 'Past due',
  },
  statusReadOnly: {
    id: 'page.subscription.statusReadOnly',
    defaultMessage: 'Read-only',
  },
  statusSuspended: {
    id: 'page.subscription.statusSuspended',
    defaultMessage: 'Suspended',
  },
  statusCancelled: {
    id: 'page.subscription.statusCancelled',
    defaultMessage: 'Cancelled',
  },
  statusExpired: {
    id: 'page.subscription.statusExpired',
    defaultMessage: 'Expired',
  },
  statusPaymentRequired: {
    id: 'page.subscription.statusPaymentRequired',
    defaultMessage: 'Payment method needed',
  },

  // --- Trial limits (DEMO only) ---
  // On paid plans `asset_count` and `addon_PRINT_EXPORT` are fair use and are
  // NOT shown at all. On DEMO both are hard caps enforced by the plan's
  // `block` mode, so a trial has to be able to see what it is about to hit.
  // They are capped, never billed — hence their own pill, not "Billed".
  trialLimitsHeading: {
    id: 'page.subscription.trialLimitsHeading',
    defaultMessage: 'Trial limits',
  },
  trialLimitsSubtitle: {
    id: 'page.subscription.trialLimitsSubtitle',
    defaultMessage:
      'Hard limits on the free trial — you are never charged. At any trial limit, '
      + 'uploads and changes pause (deleting still works). Choosing a plan lifts them.',
  },
  // Same limits, but with no plan to choose (every tier retired — how
  // self-serve is paused for a pricing review). The last sentence is the only
  // difference, and it is the whole point of the paragraph: a trial reader
  // hitting a wall needs to know what removes it.
  trialLimitsSubtitleNoPlans: {
    id: 'page.subscription.trialLimitsSubtitleNoPlans',
    defaultMessage:
      'Hard limits on the free trial — you are never charged. At any trial limit, '
      + 'uploads and changes pause (deleting still works). Talk to us if you need '
      + 'them lifted.',
  },
  trialLimitTag: {
    id: 'page.subscription.trialLimitTag',
    defaultMessage: 'Trial limit',
  },
  usageAssets: {
    id: 'page.subscription.usageAssets',
    defaultMessage: 'Assets',
  },
  usagePrintExport: {
    id: 'page.subscription.usagePrintExport',
    defaultMessage: 'Print export',
  },

  // Usage section
  usageSectionTitle: {
    id: 'page.subscription.usageSectionTitle',
    defaultMessage: 'Usage this cycle',
  },
  // The cycle's accrued metered charges — "estimated" and "so far" both
  // matter: cycle close nets AI drift against top-ups and applies the floor.
  usageEstimatedCost: {
    id: 'page.subscription.usageEstimatedCost',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: 'Estimated usage charges so far: ${amount} — billed at cycle close.',
  },
  usageEstimatedCostFloorNote: {
    id: 'page.subscription.usageEstimatedCostFloorNote',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: 'Active months bill at least ${minimum}, fully applied to usage.',
  },
  currentCycleLabel: {
    id: 'page.subscription.currentCycleLabel',
    defaultMessage: 'Current billing cycle',
  },
  currentCycleRange: {
    id: 'page.subscription.currentCycleRange',
    defaultMessage: '{start} – {end}',
  },
  currentCycleNotSet: {
    id: 'page.subscription.currentCycleNotSet',
    defaultMessage:
      'Cycle window not set yet — billing data shown is approximate.',
  },
  usageStorage: {
    id: 'page.subscription.usageStorage',
    defaultMessage: 'Storage',
  },
  usageUsers: {
    id: 'page.subscription.usageUsers',
    defaultMessage: 'Users (seats)',
  },
  usageVideo: {
    id: 'page.subscription.usageVideo',
    defaultMessage: 'Video transcode minutes',
  },
  usageAiCredits: {
    id: 'page.subscription.usageAiCredits',
    defaultMessage: 'Credits',
  },
  usageCdn: {
    id: 'page.subscription.usageCdn',
    defaultMessage: 'CDN delivery',
  },
  usageCollections: {
    id: 'page.subscription.usageCollections',
    defaultMessage: 'Collections',
  },
  usageShares: {
    id: 'page.subscription.usageShares',
    defaultMessage: 'Shares',
  },
  cdnCycleWindow: {
    id: 'page.subscription.cdnCycleWindow',
    defaultMessage: 'Current billing cycle (estimate)',
  },
  unlimited: {
    id: 'page.subscription.unlimited',
    defaultMessage: 'Unlimited',
  },
  ofQuota: {
    id: 'page.subscription.ofQuota',
    defaultMessage: 'of {quota}',
  },
  videoMinutesUnit: {
    id: 'page.subscription.videoMinutesUnit',
    defaultMessage: 'min',
  },
  aiCreditsRemaining: {
    id: 'page.subscription.aiCreditsRemaining',
    defaultMessage: '{balance} remaining',
  },
  aiCreditsRemainingLabel: {
    id: 'page.subscription.aiCreditsRemainingLabel',
    defaultMessage: 'credits remaining',
  },
  // Breakdown of the balance by source (plan allowance vs purchased top-ups).
  aiCreditsBreakdown: {
    id: 'page.subscription.aiCreditsBreakdown',
    defaultMessage: '{plan} from your plan + {topup} from top-ups',
  },
  aiCreditsFromPlan: {
    id: 'page.subscription.aiCreditsFromPlan',
    defaultMessage: 'Included with your plan',
  },
  aiCreditsMonthlyNote: {
    id: 'page.subscription.aiCreditsMonthlyNote',
    defaultMessage: 'Plan includes {quota} credits / month',
  },
  aiCreditsTrialNote: {
    id: 'page.subscription.aiCreditsTrialNote',
    defaultMessage: 'Your free trial includes {quota} credits',
  },
  // Face recognition is deliberately absent: it is a Scale/Enterprise
  // entitlement that ships added-but-DISABLED, so listing it here as something
  // credits buy would be wrong for almost every reader of this card.
  aiCreditsIncludes: {
    id: 'page.subscription.aiCreditsIncludes',
    defaultMessage:
      'Includes background removal, image analysis, AI edits, AI Agent and MCP.',
  },
  // Paid plans with print export: exports past the monthly allowance draw on the
  // same balance (priced as one background removal). PAYG has no print export
  // and a trial stops at the cap, so both keep the line above.
  aiCreditsIncludesWithPrint: {
    id: 'page.subscription.aiCreditsIncludesWithPrint',
    defaultMessage:
      'Includes background removal, image analysis, AI edits, AI Agent, MCP and '
      + 'print exports beyond your monthly allowance.',
  },
  // conf/22: on self-serve plans video processing is paid from the same
  // balance (per minute of uploaded video).
  aiCreditsIncludesVideo: {
    id: 'page.subscription.aiCreditsIncludesVideo',
    defaultMessage:
      'Includes video processing, background removal, image analysis, AI edits, AI Agent and MCP.',
  },
  aiCreditsIncludesVideoWithPrint: {
    id: 'page.subscription.aiCreditsIncludesVideoWithPrint',
    defaultMessage:
      'Includes video processing, background removal, image analysis, AI edits, AI Agent, MCP '
      + 'and print exports beyond your monthly allowance.',
  },

  // Buy AI credits (top-up packs)
  buyCreditsSectionTitle: {
    id: 'page.subscription.buyCreditsSectionTitle',
    defaultMessage: 'Buy credits',
  },
  buyCreditsSectionSubtitle: {
    id: 'page.subscription.buyCreditsSectionSubtitle',
    defaultMessage:
      'Top up your balance with a one-time purchase. Top-up credits are valid for {validityDays} days.',
  },
  buyCreditsCardCredits: {
    id: 'page.subscription.buyCreditsCardCredits',
    defaultMessage: '{credits} credits',
  },
  buyCreditsCardPrice: {
    id: 'page.subscription.buyCreditsCardPrice',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: '${price}',
  },
  buyCreditsCta: {
    id: 'page.subscription.buyCreditsCta',
    defaultMessage: 'Buy',
  },
  buyCreditsStarting: {
    id: 'page.subscription.buyCreditsStarting',
    defaultMessage: 'Opening checkout…',
  },
  buyCreditsError: {
    id: 'page.subscription.buyCreditsError',
    defaultMessage:
      "Couldn't start checkout. Please try again or contact FileSpin support.",
  },
  topupSuccess: {
    id: 'page.subscription.topupSuccess',
    defaultMessage: '{credits} credits added to your balance.',
  },
  upgradeSuccess: {
    id: 'page.subscription.upgradeSuccess',
    defaultMessage: "You're now on the {plan} plan.",
  },


  // Banners
  readOnlyBannerReadOnly: {
    id: 'page.subscription.readOnlyBannerReadOnly',
    defaultMessage:
      'Your account is in read-only mode. Reads continue to work; new uploads, edits, and deletes are paused. Contact FileSpin support.',
  },
  readOnlyBannerSuspended: {
    id: 'page.subscription.readOnlyBannerSuspended',
    defaultMessage:
      'Your account is suspended. Reads continue to work; writes are blocked. Contact FileSpin support to restore access.',
  },
  readOnlyBannerExpired: {
    id: 'page.subscription.readOnlyBannerExpired',
    defaultMessage:
      'Your subscription has expired. Reads continue to work; writes are blocked. Contact FileSpin support to renew.',
  },
  paymentRequiredBanner: {
    id: 'page.subscription.paymentRequiredBanner',
    defaultMessage:
      "Your trial has ended and you're now on Pay as you go (no monthly fee — you pay only for what you use). Your assets are safe and viewable; add a payment method to resume uploads and processing.",
  },
  addPaymentMethodCta: {
    id: 'page.subscription.addPaymentMethodCta',
    defaultMessage: 'Add payment method',
  },
  pastDueBanner: {
    id: 'page.subscription.pastDueBanner',
    defaultMessage:
      'Your last payment failed. Update your billing details to keep your account active.',
  },
  // --- Monthly minimum (PAYG) — pricing decision 2026-08-12 ---
  //
  // ⚠️ COPY RULE: the floor is never called a "minimum fee" or a "minimum
  // invoice" on any customer-facing surface. It is presented as value that goes
  // towards usage. The blunt version ("active months bill actual usage or $5,
  // whichever is higher; months you don't use FileSpin cost nothing") is
  // published in the pricing FAQ, and only there.
  planFromPerMonth: {
    id: 'page.subscription.planFromPerMonth',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: 'From ${minimum}/mo, fully applied to your usage',
  },
  // The overview card folds the floor into the plan NAME (one line, no
  // separate price span). "min" is acceptable here because "fully applied to
  // your usage" rides in the same breath — the rule above still bans
  // "minimum fee"/"minimum invoice".
  planNameWithMinimum: {
    id: 'page.subscription.planNameWithMinimum',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: '{name} (with ${minimum}/mo min, fully applied to your usage)',
  },
  rateCardMinimumNote: {
    id: 'page.subscription.rateCardMinimumNote',
    defaultMessage:
      // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
      'No commitment. ${minimum} minimum a month, counted toward your usage.',
  },
  // Dormant: an idle cycle billed $0 and paused the account. NOT delinquent —
  // the card is on file and nothing is owed — so no payment CTA here.
  dormantBanner: {
    id: 'page.subscription.dormantBanner',
    defaultMessage:
      "Your account is paused. You weren't charged for the cycle that ended "
      + '{dormantSince, date, medium} — there was no activity. Upload or process anything and '
      + 'it resumes immediately; your payment method is still on file.',
  },
  dormantBannerNoDate: {
    id: 'page.subscription.dormantBannerNoDate',
    defaultMessage:
      "Your account is paused. You weren't charged for a cycle with no activity. Upload or "
      + 'process anything and it resumes immediately; your payment method is still on file.',
  },
  // Over the free dormant storage allowance: a paused month is NOT free for
  // this account, and saying "you weren't charged" would be a lie it discovers
  // on the invoice.
  dormantBannerBilledStorage: {
    id: 'page.subscription.dormantBannerBilledStorage',
    defaultMessage:
      'Your account is paused — no processing was billed for the last cycle. You store more '
      + 'than the {allowance} GB included while paused, so stored data above that is still '
      + 'billed at the list rate. Upload or process anything and the account resumes '
      + 'immediately.',
  },
  statusDormant: {
    id: 'page.subscription.statusDormant',
    defaultMessage: 'Paused',
  },

  // --- Upgrade nudge ---
  // Shown only when a plan is genuinely cheaper for this cycle's usage (the
  // server computes the comparison and omits it otherwise), so the UI never has
  // to decide whether the suggestion is honest.
  nudgeHeading: {
    id: 'page.subscription.nudgeHeading',
    defaultMessage: 'A plan would be cheaper this month',
  },
  nudgeBody: {
    id: 'page.subscription.nudgeBody',
    defaultMessage:
      // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
      "This cycle's usage costs ${current} as you go. The same usage on {planName} would be "
      // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
      + '${alternative} — about ${saving} less.',
  },
  nudgeCta: {
    id: 'page.subscription.nudgeCta',
    defaultMessage: 'Compare plans',
  },

  // --- Spend limit (the customer-set cap on metered charges) ---
  spendCapTitle: {
    id: 'page.subscription.spendCapTitle',
    defaultMessage: 'Spend limit',
  },
  spendCapSubtitle: {
    id: 'page.subscription.spendCapSubtitle',
    defaultMessage:
      "Cap what you can be charged in a cycle. We'll email you at 75% and again when you "
      + 'reach it.',
  },
  // Said plainly, because this is the half of the feature a customer cannot
  // guess and would otherwise discover as an outage. The reassurance is not
  // padding: "uploads stopped" reads as data loss unless you say it isn't.
  spendCapServiceNote: {
    id: 'page.subscription.spendCapServiceNote',
    defaultMessage:
      'At 100% we pause uploads and processing until you raise the limit or the cycle '
      + 'resets. Your assets stay available, and downloads and delivery keep working.',
  },
  // The limit covers the plan fee too, so on a paid plan the floor is the plan
  // price. Explaining that where the number is beats a 400 the reader has to
  // interpret.
  spendCapIncludesPlanFee: {
    id: 'page.subscription.spendCapIncludesPlanFee',
    defaultMessage:
      // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
      'Your limit covers your whole bill, including your ${fee} plan fee '
      // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
      + '— so it cannot be set below ${minimum}.',
  },
  // The live position against the limit. Shown whenever a limit is set: a
  // limit you cannot see yourself approaching is one you only notice when it
  // stops you.
  spendCapProgress: {
    id: 'page.subscription.spendCapProgress',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: '${spend} of ${cap} used this cycle ({pct}%)',
  },
  spendCapProgressLabel: {
    id: 'page.subscription.spendCapProgressLabel',
    defaultMessage: 'Spend against your limit this cycle',
  },
  // Shown in the panel when the limit is currently stopping work. The banner
  // says the same thing at the top of the page; this one sits next to the
  // input that fixes it.
  spendCapReachedNote: {
    id: 'page.subscription.spendCapReachedNote',
    defaultMessage:
      'Uploads and processing are paused. Raise the limit below, or move to a plan that '
      + 'covers this level of usage, to resume.',
  },
  spendCapNone: {
    id: 'page.subscription.spendCapNone',
    defaultMessage: 'No limit set',
  },
  spendCapCurrent: {
    id: 'page.subscription.spendCapCurrent',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: 'Current limit: ${amount} per cycle',
  },
  spendCapPlaceholder: {
    id: 'page.subscription.spendCapPlaceholder',
    defaultMessage: 'Amount in USD',
  },
  spendCapSave: {
    id: 'page.subscription.spendCapSave',
    defaultMessage: 'Set limit',
  },
  spendCapClear: {
    id: 'page.subscription.spendCapClear',
    defaultMessage: 'Remove limit',
  },
  spendCapSaved: {
    id: 'page.subscription.spendCapSaved',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: 'Spend limit set to ${amount} per cycle.',
  },
  spendCapCleared: {
    id: 'page.subscription.spendCapCleared',
    defaultMessage: 'Spend limit removed.',
  },
  spendCapMinimum: {
    id: 'page.subscription.spendCapMinimum',
    // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
    defaultMessage: 'The limit cannot be lower than ${minimum}.',
  },
  // Page-level banner: the account is blocked RIGHT NOW by the customer's own
  // setting. It must never fall through to the generic write-blocked copy,
  // which tells them to contact support about a limit they set themselves.
  spendCapBlockedBanner: {
    id: 'page.subscription.spendCapBlockedBanner',
    defaultMessage:
      // eslint-disable-next-line no-template-curly-in-string -- ICU placeholder, not a JS template literal
      'Uploads and processing are paused — this cycle has reached the ${cap} spend limit you '
      + 'set. Raise the limit or upgrade to resume. Your assets are safe, and downloads and '
      + 'delivery are unaffected.',
  },
  spendCapBlockedCta: {
    id: 'page.subscription.spendCapBlockedCta',
    defaultMessage: 'Change spend limit',
  },
  spendCapError: {
    id: 'page.subscription.spendCapError',
    defaultMessage: "That didn't work. Check the amount and try again.",
  },

  // Error states
  forbidden: {
    id: 'page.subscription.forbidden',
    defaultMessage:
      'Not available — only the subscription owner and group admins can view this.',
  },
  notFound: {
    id: 'page.subscription.notFound',
    defaultMessage: 'Subscription not found.',
  },
  loadError: {
    id: 'page.subscription.loadError',
    defaultMessage: 'Could not load subscription details.',
  },
  retry: {
    id: 'page.subscription.retry',
    defaultMessage: 'Retry',
  },
});
